// src/features/goal/pages/GoalsPage.tsx
// Goals 列表頁面（內部實現為 Objectives）- 極簡重構版本
//
// 術語說明 (2025-12-03)：
// - UI 層：使用 "Goals"（面向用戶，更親和、減少績效考核聯想）
// - 代碼層：使用 "Objective"（內部實現，保持技術一致性）
// - 路由改用 /goals（用戶友好），但保留 /objectives 作為別名路由
// - API 和資料庫均使用 "objective"（零風險，無需遷移）
//
// 架構更新 (2025-12-27)：
// - 改用本地優先架構（Local-first）
// - 使用 ObjectiveContext 管理狀態
// - 離線可操作，背景自動同步

import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useOnboarding, TipTooltip, useTipAutoMarker } from '../../onboarding';
import {
  Plus,
  ArrowLeft,
  Milestone,
  Check,
  Trophy,
  Pencil,
  Pause,
  Play,
  Trash2,
  WifiOff,
} from "lucide-react";
import LoadingSpinner from "../../../components/common/LoadingSpinner";
import ErrorState from "../../../components/common/ErrorState";
import EmptyState from "../../../components/common/EmptyState";
import { LocalObjective } from "../services/indexedDB/objectiveRepository";
import { useObjectives } from "../contexts/ObjectiveContext";
import { logger } from "../../../utils/logger";
import {
  calculateObjectiveProgress,
  getCategoryConfig,
} from "../utils/objectiveUtils";
import ObjectiveCreateModal from "../components/ObjectiveCreateModal";
import { useHeader } from "../../../hooks/useHeader";
import { ConfettiCelebration } from "../../../components/dayClose/ConfettiCelebration";
import { eventBus, EVENTS } from "../../../utils/eventBus";

/**
 * Goals 列表頁面（內部實現為 Objectives）
 *
 * 術語約定：
 * - UI 顯示：使用 "Goals"（用戶友好）
 * - 代碼實現：使用 "Objective"（技術一致性）
 *
 * 設計原則：
 * - 浮動進度條：使用 tier 系統，永不到達 100%
 * - Section Headers：取代過濾器，顯示數量統計
 * - 響應式網格：1/2/3 列佈局
 */
const Objectives: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getActiveTipForPage, dismissAndShowNext, startLesson3Guide } = useOnboarding();

  // Lesson 3 引導：從 LessonsPanel 導航至 Goals 時帶有 lessonId state
  const incomingLessonId = (location.state as { lessonId?: string } | null)?.lessonId;
  useTipAutoMarker();
  const activeTip = getActiveTipForPage('goals');

  // 使用 ObjectiveContext（本地優先架構）
  const {
    objectives,
    loading,
    error,
    isOnline,
    activeObjectives: active,
    pausedObjectives: paused,
    completedObjectives: completed,
    updateObjective: contextUpdateObjective,
    deleteObjective: contextDeleteObjective,
    pauseObjective: contextPauseObjective,
    resumeObjective: contextResumeObjective,
    completeObjective: contextCompleteObjective,
    refreshObjectives,
    triggerPageViewSync,
  } = useObjectives();

  // 頁面進入時觸發智慧同步（無冷卻限制）
  useEffect(() => {
    triggerPageViewSync();
  }, [triggerPageViewSync]);

  // Lesson 3 引導啟動
  const lesson3Started = useRef(false);
  useEffect(() => {
    if (incomingLessonId !== 'lesson-3') return;
    if (lesson3Started.current) return;
    lesson3Started.current = true;
    startLesson3Guide();
  }, [incomingLessonId, startLesson3Guide]);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const openCreateModal = useCallback(() => {
    setIsCreateModalOpen(true);
    eventBus.publish(EVENTS.GOAL.CREATE_MODAL_OPENED);
  }, []);

  // 完成目標相關狀態
  const [objectiveToComplete, setObjectiveToComplete] =
    useState<LocalObjective | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  // Menu 和操作狀態
  const [objectiveToEdit, setObjectiveToEdit] = useState<LocalObjective | null>(
    null,
  );
  const [objectiveToDelete, setObjectiveToDelete] = useState<LocalObjective | null>(
    null,
  );
  const [editTitle, setEditTitle] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null); // 展開狀態（行動 + 桌面，僅用於操作按鈕）

  // 配置 Header - 使用 useMemo 穩定引用，避免無限重新渲染
  const headerConfig = useMemo(
    () => ({
      title: "Goals",
      leftAction: (
        <button
          onClick={() => navigate(-1)}
          className="md:hidden p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Go back"
        >
          <ArrowLeft size={20} className="text-gray-600" />
        </button>
      ),
      rightActions: [
        // Achievements 按鈕（始終顯示，badge 只在有完成時顯示）
        <button
          onClick={() => navigate("/goals/achievements")}
          className="relative p-2 rounded-lg hover:bg-sky-100 transition-colors text-sky-600 hover:text-sky-700"
          key="achievements"
          aria-label="View Achievements"
        >
          <Trophy size={20} />
          {/* Badge - 只在 completed > 0 時顯示 */}
          {completed.length > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px]
                           bg-sky-500 text-white text-xs font-bold
                           rounded-full flex items-center justify-center px-1"
            >
              {completed.length}
            </span>
          )}
        </button>,
        // 原有的 "+" 按鈕
        <button
          onClick={openCreateModal}
          className="p-2 rounded-lg hover:bg-sky-100 transition-colors text-sky-600 hover:text-sky-700"
          key="new-objective"
          aria-label="New Goal"
        >
          <Plus size={20} />
        </button>,
      ],
    }),
    [navigate, completed.length, openCreateModal],
  ); // 依賴 completed.length 以更新 badge

  useHeader(headerConfig);

  // 處理完成目標（本地優先 - 立即響應）
  const handleCompleteObjective = async () => {
    if (!objectiveToComplete) return;

    try {
      setIsCompleting(true);
      logger.dev("Objectives: Completing objective", {
        id: objectiveToComplete.id,
      });

      // 本地優先：立即完成，背景同步
      await contextCompleteObjective(objectiveToComplete.id);

      // 觸發慶祝動畫
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);

      // 關閉對話框
      setObjectiveToComplete(null);

      logger.dev("Objectives: Objective completed successfully");
    } catch (err) {
      logger.error("Objectives: Failed to complete objective", err);
    } finally {
      setIsCompleting(false);
    }
  };

  // 處理暫停目標（本地優先）
  const handlePauseObjective = async (objectiveId: string) => {
    try {
      logger.dev("Objectives: Pausing objective", { id: objectiveId });
      await contextPauseObjective(objectiveId);
      setExpandedId(null);
      logger.dev("Objectives: Objective paused successfully");
    } catch (err) {
      logger.error("Objectives: Failed to pause objective", err);
    }
  };

  // 處理恢復目標（本地優先）
  const handleResumeObjective = async (objective: LocalObjective) => {
    try {
      logger.dev("Objectives: Resuming objective", { id: objective.id });
      await contextResumeObjective(objective.id);
      setExpandedId(null);
      logger.dev("Objectives: Objective resumed successfully");
    } catch (err) {
      // 檢查是否是 Active 數量限制錯誤
      if (err instanceof Error && err.message.includes("active goals")) {
        setResumeError("You have 3 active goals. Pause one to resume this goal.");
      } else {
        logger.error("Objectives: Failed to resume objective", err);
      }
    }
  };

  // 開啟編輯對話框
  const openEditDialog = (objective: LocalObjective) => {
    setObjectiveToEdit(objective);
    setEditTitle(objective.title);
    setExpandedId(null);
  };

  // 處理編輯目標（本地優先）
  const handleEditObjective = async () => {
    if (!objectiveToEdit || !editTitle.trim()) return;

    try {
      setIsEditing(true);
      logger.dev("Objectives: Editing objective", { id: objectiveToEdit.id });
      await contextUpdateObjective(objectiveToEdit.id, {
        title: editTitle.trim(),
      });
      setObjectiveToEdit(null);
      setEditTitle("");
      logger.dev("Objectives: Objective edited successfully");
    } catch (err) {
      logger.error("Objectives: Failed to edit objective", err);
    } finally {
      setIsEditing(false);
    }
  };

  // 開啟刪除確認對話框
  const openDeleteDialog = (objective: LocalObjective) => {
    setObjectiveToDelete(objective);
    setExpandedId(null);
  };

  // 處理刪除目標（軟刪除，本地優先）
  const handleDeleteObjective = async () => {
    if (!objectiveToDelete) return;

    try {
      setIsDeleting(true);
      logger.dev("Objectives: Deleting objective", {
        id: objectiveToDelete.id,
      });
      await contextDeleteObjective(objectiveToDelete.id);
      setObjectiveToDelete(null);
      logger.dev("Objectives: Objective deleted successfully");
    } catch (err) {
      logger.error("Objectives: Failed to delete objective", err);
    } finally {
      setIsDeleting(false);
    }
  };

  // 渲染 Active Objective 卡片（Apple 風格 - 左右分欄）
  const renderActiveCard = (objective: LocalObjective) => {
    const categoryConfig = getCategoryConfig(objective.category);
    const CategoryIcon = categoryConfig.icon;
    const progress = calculateObjectiveProgress(objective.totalRipples);

    // 點擊導航到 Goal Detail 頁面
    const handleCardClick = () => {
      eventBus.publish(EVENTS.UI.GOAL_ACTIONS_OPENED);
      navigate(`/goals/${objective.id}`);
    };

    return (
      <div key={objective.id} className="group relative">
        {/* 卡片本體 */}
        <div
          onClick={handleCardClick}
          className="bg-white rounded-xl p-[18px]
                     shadow-sm hover:shadow-md transition-shadow duration-200
                     min-h-[140px] cursor-pointer"
        >
          {/* 左右分欄佈局 */}
          <div className="flex gap-3">
            {/* 左欄：Icon（固定寬度，垂直置中） */}
            <div className="flex-shrink-0 w-14 flex justify-center">
              {/* 彩色方形背景 */}
              <div
                className={`${categoryConfig.bgColor} rounded-xl p-2.5
                            flex justify-center h-14`}
              >
                <CategoryIcon
                  size={32}
                  className={categoryConfig.iconColor}
                  strokeWidth={2}
                />
              </div>
            </div>

            {/* 右欄：上下堆疊（標題、進度條、Ripples） */}
            <div className="flex-1 min-w-0 flex flex-col gap-3.5 m-2">
              {/* Row 1: 標題 */}
              <h3
                className="text-lg font-semibold text-gray-900
                           leading-tight tracking-wider
                           line-clamp-2 h-12 mb-2 mt-1"
              >
                {objective.title}
              </h3>

              {/* Row 2: 進度條 */}
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden relative">
                <div
                  className="h-full bg-gradient-to-r from-sky-400 to-sky-500
                           rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                >
                  {/* Subtle shimmer */}
                  <div
                    className="h-full w-full bg-gradient-to-r from-transparent
                                via-white/20 to-transparent animate-shimmer"
                  />
                </div>
              </div>

              {/* Row 3: Ripples */}
              <div className="flex items-baseline gap-1.5">
                <span className="text-sm font-semibold text-gray-900 tabular-nums">
                  {objective.totalRipples.toLocaleString()}
                </span>
                <span className="text-xs font-medium text-gray-500">
                  Ripples
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 底部浮動按鈕列 - 桌面 hover 顯示 */}
        <div
          className={`
          flex justify-around items-center gap-2 mt-2 pt-2
          transition-all duration-200 overflow-hidden
          max-h-0 opacity-0 md:group-hover:max-h-12 md:group-hover:opacity-100
        `}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              setObjectiveToComplete(objective);
            }}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg
                       hover:bg-green-50 text-gray-500 hover:text-green-600
                       transition-colors duration-150"
            aria-label="Complete"
          >
            <Check size={18} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handlePauseObjective(objective.id);
            }}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg
                       hover:bg-yellow-50 text-gray-500 hover:text-yellow-600
                       transition-colors duration-150"
            aria-label="Pause"
          >
            <Pause size={18} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              openEditDialog(objective);
            }}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg
                       hover:bg-sky-50 text-gray-500 hover:text-sky-600
                       transition-colors duration-150"
            aria-label="Edit"
          >
            <Pencil size={18} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              openDeleteDialog(objective);
            }}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg
                       hover:bg-red-50 text-gray-500 hover:text-red-600
                       transition-colors duration-150"
            aria-label="Delete"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
    );
  };

  // 渲染 Paused Objective List Item（極簡版）
  const renderPausedItem = (objective: LocalObjective) => {
    const isExpanded = expandedId === `paused-${objective.id}`;

    // 行動端點擊切換展開狀態
    const handleItemClick = () => {
      setExpandedId(isExpanded ? null : `paused-${objective.id}`);
    };

    return (
      <div key={objective.id} className="group">
        {/* 項目本體 */}
        <div
          onClick={handleItemClick}
          className="px-4 py-3.5 bg-white hover:bg-gray-50
                     transition-colors duration-150 cursor-pointer md:cursor-default"
        >
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-[15px] font-medium text-gray-900 leading-snug line-clamp-1 flex-1">
              {objective.title}
            </h3>
            <div className="text-sm font-semibold text-sky-500 whitespace-nowrap">
              {objective.totalRipples.toLocaleString()}
            </div>
          </div>
        </div>

        {/* 底部浮動按鈕 - 桌面 hover 顯示，行動端展開時顯示，靠右對齊 */}
        <div
          className={`
          flex justify-end items-center gap-1 px-2
          transition-all duration-200 overflow-hidden
          ${isExpanded ? "max-h-10 opacity-100" : "max-h-0 opacity-0 md:group-hover:max-h-10 md:group-hover:opacity-100"}
        `}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleResumeObjective(objective);
            }}
            className="p-2 rounded-lg hover:bg-green-50 text-gray-400 hover:text-green-600
                       transition-colors duration-150"
            aria-label="Resume"
          >
            <Play size={16} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              openEditDialog(objective);
            }}
            className="p-2 rounded-lg hover:bg-sky-50 text-gray-400 hover:text-sky-600
                       transition-colors duration-150"
            aria-label="Edit"
          >
            <Pencil size={16} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              openDeleteDialog(objective);
            }}
            className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600
                       transition-colors duration-150"
            aria-label="Delete"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    );
  };

  // 渲染空狀態
  const renderEmptyState = () => (
    <EmptyState
      icon={Milestone}
      title="No Goals Yet"
      action={{
        label: 'New Goal',
        onClick: openCreateModal,
        'data-lesson-anchor': 'goal-new-goal',
      }}
    />
  );

  return (
    <div className="content-standard">
      {/* 離線狀態指示器 */}
      {!isOnline && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg mb-4">
          <WifiOff size={16} className="flex-shrink-0" />
          <span>You're offline. Changes will sync when you're back online.</span>
        </div>
      )}

      {/* 錯誤提示 */}
      {error && (
        <ErrorState message={error} className="mb-6" />
      )}

      {/* Loading 狀態 */}
      {loading ? (
        <LoadingSpinner size="lg" className="py-16" />
      ) : active.length === 0 && paused.length === 0 ? (
        renderEmptyState()
      ) : (
        <div className="space-y-8">
          {/* Active Objectives Section */}
          {active.length > 0 && (
            <section>
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                Active
                <span className="ml-2 text-sm font-normal text-gray-500">
                  {active.length}/3
                </span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {active.slice(0, 3).map((obj, index) => {
                  const card = renderActiveCard(obj);
                  // 添加 Tips 到第一張卡片
                  if (index === 0) {
                    return (
                      <div key={obj.id} className="relative" data-lesson-anchor="goal-card">
                        {card}
                        {/* Tip: Ripples System */}
                        {activeTip === 'goals-ripples-system' && (
                          <TipTooltip
                            tipId="goals-ripples-system"
                            onClose={() => dismissAndShowNext('goals-ripples-system', 'goals')}
                          />
                        )}
                        {/* Tip: Card Actions */}
                        {activeTip === 'goals-card-actions' && (
                          <TipTooltip
                            tipId="goals-card-actions"
                            onClose={() => dismissAndShowNext('goals-card-actions', 'goals')}
                          />
                        )}
                      </div>
                    );
                  }
                  return card;
                })}
              </div>
            </section>
          )}

          {/* Paused Objectives Section */}
          {paused.length > 0 && (
            <section>
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                Paused
                <span className="ml-2 text-sm font-normal text-gray-500">
                  {paused.length}
                </span>
              </h2>
              <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
                {paused.map(renderPausedItem)}
              </div>
            </section>
          )}
        </div>
      )}

      {/* Objective 創建 Modal */}
      <ObjectiveCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          eventBus.publish(EVENTS.GOAL.CREATE_MODAL_CLOSED);
        }}
        onSuccess={() => {
          refreshObjectives();
          eventBus.publish(EVENTS.GOAL.CREATED);
        }}
      />

      {/* 完成確認對話框 */}
      {objectiveToComplete &&
        (() => {
          const categoryConfig = getCategoryConfig(
            objectiveToComplete.category,
          );
          const CategoryIcon = categoryConfig.icon;

          return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-modal p-4">
              <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 animate-scale-in">
                {/* Header */}
                <div className="text-center mb-6">
                  <div
                    className="w-16 h-16 bg-gradient-to-br from-sky-100 to-pink-100
                              rounded-full flex items-center justify-center mx-auto mb-4"
                  >
                    <Check size={32} className="text-sky-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Complete Goal?
                  </h3>
                  <p className="text-gray-600 text-sm">
                    You're about to mark this goal as completed
                  </p>
                </div>

                {/* Goal Info - 保留分類 Icon */}
                <div
                  className="bg-gradient-to-br from-sky-50 to-pink-50
                            rounded-xl p-4 mb-6 border-2 border-sky-200"
                >
                  <div className="flex gap-3 items-start">
                    {/* Icon */}
                    <div className="flex-shrink-0">
                      <div
                        className={`${categoryConfig.bgColor} rounded-lg p-2
                                  flex items-center justify-center`}
                      >
                        <CategoryIcon
                          size={24}
                          className={categoryConfig.iconColor}
                          strokeWidth={2}
                        />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                        {objectiveToComplete.title}
                      </h4>
                      <div className="flex items-center gap-3 text-sm text-gray-600">
                        <span className="font-semibold text-sky-600">
                          {objectiveToComplete.totalRipples.toLocaleString()}{" "}
                          Ripples
                        </span>
                        <span>•</span>
                        <span>Week {objectiveToComplete.currentWeek}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setObjectiveToComplete(null)}
                    disabled={isCompleting}
                    className="btn-ghost flex-1 py-3 rounded-xl"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCompleteObjective}
                    disabled={isCompleting}
                    className="flex-1 px-4 py-3 rounded-xl
                         bg-gradient-to-r from-sky-500 to-pink-500
                         hover:from-sky-600 hover:to-pink-600
                         text-white font-semibold
                         transition-all duration-200
                         disabled:opacity-50 disabled:cursor-not-allowed
                         shadow-md hover:shadow-lg"
                  >
                    {isCompleting ? "Completing..." : "Complete Goal"}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {/* 編輯對話框 */}
      {objectiveToEdit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-modal p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 animate-scale-in">
            {/* Header */}
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-900">Edit Goal</h3>
              <p className="text-gray-500 text-sm mt-1">
                Update your goal title
              </p>
            </div>

            {/* Input */}
            <div className="mb-6">
              <label
                htmlFor="edit-title"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Title
              </label>
              <input
                id="edit-title"
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-300
                           focus:border-sky-500 focus:ring-2 focus:ring-sky-200
                           outline-none transition-all duration-200"
                placeholder="Enter goal title"
                autoFocus
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setObjectiveToEdit(null);
                  setEditTitle("");
                }}
                disabled={isEditing}
                className="btn-ghost flex-1 py-3 rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={handleEditObjective}
                disabled={
                  isEditing ||
                  !editTitle.trim() ||
                  editTitle.trim() === objectiveToEdit.title
                }
                className="flex-1 px-4 py-3 rounded-xl
                           bg-sky-500 hover:bg-sky-600
                           text-white font-semibold
                           transition-colors duration-200
                           disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isEditing ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 刪除確認對話框 */}
      {objectiveToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-modal p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 animate-scale-in">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 size={32} className="text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Delete Goal?
              </h3>
              <p className="text-gray-600 text-sm">
                This goal will be archived. You can't undo this action.
              </p>
            </div>

            {/* Goal Info */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-200">
              <h4 className="font-semibold text-gray-900 line-clamp-2">
                {objectiveToDelete.title}
              </h4>
              <p className="text-sm text-gray-500 mt-1">
                {objectiveToDelete.totalRipples.toLocaleString()} Ripples
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => setObjectiveToDelete(null)}
                disabled={isDeleting}
                className="btn-ghost flex-1 py-3 rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteObjective}
                disabled={isDeleting}
                className="btn-danger flex-1 py-3 rounded-xl"
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resume 錯誤提示 */}
      {resumeError && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-modal p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 animate-scale-in">
            <div className="text-center">
              <div className="w-14 h-14 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Pause size={28} className="text-yellow-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Cannot Resume
              </h3>
              <p className="text-gray-600 text-sm mb-6">{resumeError}</p>
              <button
                onClick={() => setResumeError(null)}
                className="w-full px-4 py-3 rounded-xl
                           bg-gray-900 hover:bg-gray-800
                           text-white font-medium
                           transition-colors duration-200"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 慶祝動畫 */}
      <ConfettiCelebration trigger={showConfetti} />

    </div>
  );
};

export default Objectives;
